/* eslint-disable promise/param-names */
import HTTP from '@/utils/http-common';
import { hasNotCompletedInChecklist } from '@/api/checklist';
import router from '../../router';
import { IS_DEV_ENV } from '@/utils/tools';

const state = {
  token: sessionStorage.getItem('access_token_4') || localStorage.getItem('access_token') || '',
  originUserId: localStorage.getItem('origin_user_id') || '',
  status: '',
  err: {},
  hasLoadedOnce: false
};

const getters = {
  isAuthenticated: (state) => !!state.token,
  isSwitchedToUser: (state) => !!state.originUserId,
  originUserId: (state) => state.originUserId,
  authStatus: (state) => state.status,
  stateError: (state) => state.err
};

const actions = {
  update_user({ commit }, user) {
    commit('SET_USER', user);
  },
  auth_signin_request({ commit }, user) {
    commit('AUTH_REQUEST');
    return HTTP.post('/users/login', user)
      .then(async (resp) => {
        handleAuthResponse(commit, resp);
        await redirectAfterSignin(user.redirect);
      })
      .catch(clearAuthOnError(commit));
  },
  auth_signup_request({ commit }, user) {
    commit('AUTH_REQUEST');
    return HTTP.post('/users/login', user)
      .then(async (resp) => {
        handleAuthResponse(commit, resp, true);
      })
      .catch(clearAuthOnError(commit));
  },
  auth_provider_request({ commit }, payload) {
    const {
      provider, credential, signup = false, pId
    } = payload;
    commit('AUTH_REQUEST');
    return HTTP.post(`/users/login/${provider}`, { credential, signup, pId })
      .then(async (resp) => {
        handleAuthResponse(commit, resp, signup);
        if (!signup) await redirectAfterSignin(payload.redirect);
      })
      .catch(clearAuthOnError(commit));
  },
  auth_provider_link_request({ commit }, { provider, redirect, ...credentials }) {
    commit('AUTH_REQUEST');
    return HTTP.post(`/users/login/${provider}/link`, credentials)
      .then(async (resp) => {
        handleAuthResponse(commit, resp);
        await redirectAfterSignin(redirect);
      })
      .catch(clearAuthOnError(commit));
  },
  auth_switch({ commit, state, getters }, { userId, version, switchBack }) {
    // commit('AUTH_REQUEST');
    return HTTP.post('/users/switch', { userId })
      .then((resp) => {
        const { token, user } = resp.data;
        if (version === 1) {
          return window.open(
            location.origin + `?temp-token=${token}`,
            user.distributorId.name,
            `location=no,menubar=yes,toolbar=true,scrollbars=yes,status=yes,resizable=yes`
          );
        }
        localStorage.setItem('access_token', token);

        const originUserId = switchBack ? null : getters.loggedInUser._id;
        if (originUserId) {
          localStorage.setItem('origin_user_id', originUserId);
        } else {
          localStorage.removeItem('origin_user_id');
        }

        HTTP.defaults.headers.authorization = token;
        commit('SET_USER', resp.data.user);
        commit('AUTH_SUCCESS', { token, originUserId });

        if (switchBack) {
          location.href = '/users';
          // location.reload();
        } else {
          if (!user.distributorId) {
            location.href = !user.supplierId ? '/signup' : '/choose-portal';
          } else {
            location.href = '/dashboard';
          }
        }
        // window.open(location.origin + `?temp-token=${token}`, '_blank');
      })
      .catch(() => {
        commit('AUTH_SUCCESS', { token: state.token });
      });
  },
  auth_logout({ commit }) {
    commit('AUTH_LOGOUT');
    sessionStorage.removeItem('access_token_4');
    localStorage.removeItem('access_token');
    localStorage.removeItem('origin_user_id');
    localStorage.removeItem('orders');
    localStorage.removeItem('customers');
    localStorage.removeItem('products');
    if (location.href.match('/grabber')) {
    } else {
      router.push('/signin');
    }
  }
};

async function redirectAfterSignin(redirect) {
  const rawRedirect = `${redirect || ''}`;
  const oauthReturn = rawRedirect.startsWith('oauth-consent');
  // Resolve against our origin; require same-origin so ?redirect= can't bounce off-site.
  let safePath = null;
  if (rawRedirect.startsWith('/')) {
    try {
      const url = new URL(rawRedirect, window.location.origin);
      // Same-origin AND not a protocol-relative path ("//host") after normalization.
      if (url.origin === window.location.origin && !/^\/[/\\]/.test(url.pathname)) {
        safePath = url.pathname + url.search + url.hash;
      }
    } catch (e) {}
  }
  let redirectPath;
  if (oauthReturn) {
    redirectPath = { path: `/${rawRedirect.replace(/-/g, '/')}` };
  } else if (safePath) {
    redirectPath = { path: safePath };
  } else {
    redirectPath = '/choose-portal';
  }
  if (IS_DEV_ENV && !oauthReturn && !safePath) {
    redirectPath = '/choose-portal';
  }
  const isGoToQuickStartGuide = localStorage.getItem('isFirstSignin') && (await hasNotCompletedInChecklist());

  window.location.href = isGoToQuickStartGuide ? '/my-account/quick-start-guide' : redirectPath.path || redirectPath;
}

function clearAuthOnError(commit) {
  return (err) => {
    commit('AUTH_ERROR', err);
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('access_token_4');
    localStorage.removeItem('access_token');
    localStorage.removeItem('origin_user_id');
  };
}

function handleAuthResponse(commit, resp, isSignup = false) {
  const { token, env, user } = resp.data;
  localStorage.setItem('env', JSON.stringify(env));
  // sessionStorage.setItem('access_token', token);
  localStorage.setItem('access_token', token);
  localStorage.removeItem('origin_user_id');
  HTTP.defaults.headers.authorization = token;
  commit('SET_USER', user);
  commit('AUTH_SUCCESS', { token });

  if (isSignup) {
    localStorage.setItem('isFirstSignin', 'true');
  }
}

const mutations = {
  AUTH_REQUEST(state) {
    state.status = 'loading';
  },
  AUTH_SUCCESS(state, { token, originUserId }) {
    state.status = 'success';
    state.token = token;
    state.originUserId = originUserId;
    state.hasLoadedOnce = true;
  },
  AUTH_ERROR(state, error) {
    const errStatus = error.status;
    // if (errStatus === 401) {
    //   router.push(`/registration/${error.data.user._id}`);
    // }
    state.status = 'error';
    state.err.message = (error && error.data && error.data.message) || '';
    state.err.status = errStatus;
    state.err.code = error?.data?.code;
    state.err.email = error?.data?.email;
    state.err.provider = error?.data?.provider;
    state.err.name = error?.data?.name;
    state.hasLoadedOnce = true;
  },
  AUTH_LOGOUT(state) {
    state.token = '';
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
